import React, { useEffect, useMemo, useState } from 'react';

import StoreConfirmationModal from 'features/store/components/store-confirmation-modal';
import { useIntl } from 'react-intl';
import { Dimensions, GestureResponderEvent } from 'react-native';

import ActionButton from 'components/action-button';
import { CheckoutErrorsModal } from 'components/checkout-errors-modal';
import SignUpToContinueActionLink from 'experiments/checkout-v2/control/pages/cart/payment/sign-up-to-continue-action-link';
import { CheckOutStickyFooter } from 'experiments/checkout-v2/control/pages/cart/payment/sticky-footer';
import { LogUserErrorSeverity, useLogUserErrorMessage } from 'hooks/log-user-error-message';
import { useRumPageView } from 'hooks/rum/use-rum-page-view';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import { useGetPaymentErrors } from 'hooks/use-get-payment-errors';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import useTraceLoading from 'hooks/use-trace-loading';
import { useCartContext } from 'state/cart';
import { selectors, useAppSelector } from 'state/global-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { useServiceModeContext } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { routes } from 'utils/routing';

import { CartPriceTooLow, SignUpToContinueDisclaimer } from './styled';
import { IPaymentProps } from './types';
import { logCartContinue } from './utils';

// this is not payment related this is actually the cart footer
const Payment: React.FC<React.PropsWithChildren<IPaymentProps>> = ({
  disabled = false,
  serverOrder,
  offerIsValid,
  scrollViewRef,
  phoneNumberInputRef,
  shouldSkipPriceOrder,
}) => {
  useRumPageView('payment', 'Payment');

  const { formatCurrencyForLocale } = useUIContext();
  const {
    priceOrderFailure,
    isOrderPriceLoading,
    isOrderPricingRetryInProgress,
    didOrderPricingRetryFail,
    isPricingFailureRecoverable,
    tryPricing,
  } = useCartContext();

  const { calculateCartTotal } = useCart();

  const { alertOrderDeliveryMinimum } = useOrderContext();
  const { isDelivery } = useServiceModeContext();
  const [isCheckoutErrorsModalOpen, setIsCheckoutErrorsModalOpen] = useState(false);
  const [hasManualPricingBeenAttempted, setHasManualPricingAttempted] = useState(false);

  const { formatMessage } = useIntl();

  const checkoutDeliveryPriceMinimum = useFlag(LaunchDarklyFlag.OVERRIDE_CHECKOUT_DELIVERY_MINIMUM);

  const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  useEffect(alertOrderDeliveryMinimum, [alertOrderDeliveryMinimum]);

  useEffect(() => {
    if (priceOrderFailure && didOrderPricingRetryFail) {
      setIsCheckoutErrorsModalOpen(true);
    }
  }, [priceOrderFailure, didOrderPricingRetryFail]);

  const checkoutPriceMinimum = isDelivery ? checkoutDeliveryPriceMinimum : 0;

  const cartTotal = calculateCartTotal();
  const orderTotal = isSignUpAfterCartEnabled ? cartTotal : serverOrder?.cart?.totalCents ?? 0;

  const cartSubTotal =
    isSignUpAfterCartEnabled || shouldSkipPriceOrder
      ? cartTotal
      : Number(serverOrder?.cart?.subTotalCents);
  const cartPriceTooLow = cartSubTotal < checkoutPriceMinimum;

  const [StoreConfirmationDialog, , , dismissStoreConfirmationDialog] = useDialogModal({
    showCancel: true,
    Component: StoreConfirmationModal,
    modalAppearanceEventMessage: 'Confirmation Modal: Store Location',
  });

  const { shouldConfirmStoreOnCheckout } = useUIContext();

  const isLoading = (!shouldSkipPriceOrder && isOrderPriceLoading) || isLoadingOffers;

  const onStoreConfirmation = () => {
    dismissStoreConfirmationDialog();
    shouldConfirmStoreOnCheckout(true);
  };

  const { getErrorMessages } = useGetPaymentErrors({ cartPriceTooLow });

  const paymentErrorMessages = getErrorMessages(
    didOrderPricingRetryFail
      ? {
          unableToProcessOrderError: hasManualPricingBeenAttempted,
          pricedAndAvailableError: !hasManualPricingBeenAttempted,
        }
      : {
          invalidOfferError: !offerIsValid,
        }
  );

  const scrolledToFocusedInput = () => {
    phoneNumberInputRef?.current?.measure(
      (x: number, y: number, width: number, height: number, pageX: number, pageY: number) => {
        const { height: windowHeight } = Dimensions.get('window');
        const scrollToYValue = pageY > 0 ? pageY : windowHeight - Math.abs(pageY);
        scrollViewRef?.current?.scrollTo({
          y: scrollToYValue,
          animated: true,
        });

        // Workaround to open keyboard on input focus.
        setTimeout(() => {
          phoneNumberInputRef?.current?.blur();
          phoneNumberInputRef?.current?.focus();
        }, 50);
      }
    );
  };

  useTraceLoading({
    name: 'Continue',
    isLoading,
  });

  const hasErrors = !!paymentErrorMessages.length;

  const ContinueButton = useMemo(() => {
    let buttonText = formatMessage({ id: 'continue' });

    if (isOrderPricingRetryInProgress) {
      buttonText = formatMessage({ id: 'thisMayTakeAFewSeconds' });
    } else if (isLoading) {
      buttonText = formatMessage({ id: 'loadingYourOrder' });
    }

    if (hasErrors) {
      const handleErrorContinueClick = () => {
        logCartContinue();
        setIsCheckoutErrorsModalOpen(true);
      };

      return (
        <ActionButton
          onPress={handleErrorContinueClick}
          fullWidth
          isLoading={isLoading}
          noLogOnLongLoading
          disabled={
            disabled || !offerIsValid || cartPriceTooLow || shouldSkipPriceOrder || isLoading
          }
        >
          {buttonText}
        </ActionButton>
      );
    }

    const handleContinuePress = (e: GestureResponderEvent) => {
      // if loading do nothing
      if (isLoading) {
        e?.preventDefault();
        e?.stopPropagation();
        return;
      }

      logCartContinue();
    };

    return (
      <ActionButton
        onPress={handleContinuePress}
        to={routes.cartCheckout}
        fullWidth
        isLoading={isLoading}
        noLogOnLongLoading
        disabled={disabled || cartPriceTooLow || isLoading}
      >
        {buttonText}
      </ActionButton>
    );
  }, [
    isLoading,
    disabled,
    offerIsValid,
    cartPriceTooLow,
    hasErrors,
    formatMessage,
    shouldSkipPriceOrder,
    isOrderPricingRetryInProgress,
  ]);

  const cartPriceTooLowMessage = formatMessage(
    {
      id: 'deliveryMinumumNotReached',
    },
    {
      min: formatCurrencyForLocale(checkoutDeliveryPriceMinimum),
    }
  );

  // TODO FIX technically message visibility should be tied to isLoading (it flashes "minimum not reached" on page load every time)
  // but trying to not change any page logic with these error message additions to reduce risk
  // bug here: https://rbictg.atlassian.net/browse/BKPE-2611
  useLogUserErrorMessage({
    message: cartPriceTooLowMessage,
    logWhenTrue: cartPriceTooLow && !isLoading,
    severity: LogUserErrorSeverity.Recoverable,
  });

  const CTAButton = isSignUpAfterCartEnabled ? (
    <>
      <SignUpToContinueDisclaimer>
        {formatMessage({ id: 'signUpToContinueDisclaimer' })}
      </SignUpToContinueDisclaimer>
      <SignUpToContinueActionLink />
    </>
  ) : (
    ContinueButton
  );

  const priceTooLowCmp = cartPriceTooLow && !isLoading && (
    <CartPriceTooLow>{cartPriceTooLowMessage}</CartPriceTooLow>
  );

  const attemptManualOrderPricing = () => {
    setHasManualPricingAttempted(true);
    tryPricing({ isRepriceAttempt: true });
    setIsCheckoutErrorsModalOpen(false);
  };

  const shouldAllowManualPricingAttempt =
    isPricingFailureRecoverable && didOrderPricingRetryFail && !hasManualPricingBeenAttempted;

  return (
    <>
      <CheckOutStickyFooter
        priceTooLowCmp={priceTooLowCmp}
        CTAButton={CTAButton}
        isLoading={isLoading}
        orderTotal={orderTotal}
      />

      {/* modals */}
      <StoreConfirmationDialog
        onConfirm={onStoreConfirmation}
        onDismiss={dismissStoreConfirmationDialog}
      />
      {isCheckoutErrorsModalOpen && (
        <CheckoutErrorsModal
          {...(shouldAllowManualPricingAttempt
            ? {
                onCtaPress: attemptManualOrderPricing,
                ctaText: formatMessage({ id: 'tryAgain' }),
                heading: formatMessage({ id: 'oopsSomethingWentWrong' }),
              }
            : {
                heading: formatMessage({ id: 'unableToProcessOrder' }),
              })}
          checkoutErrors={paymentErrorMessages}
          onClose={() => {
            setIsCheckoutErrorsModalOpen(false);
            scrolledToFocusedInput();
          }}
        />
      )}
    </>
  );
};

export default Payment;
